import {type Brand} from '#/lib/brand/types'

/**
 * The CooeyNet mark: an upward triangle split into three red pieces (apex +
 * two lower) by a Y-shaped negative space. Authored in a 24x24 viewBox; the
 * raster favicons share this geometry (scripts/gen-brand-favicons.js).
 */
export const COOEY_MARK_VIEWBOX = '0 0 24 24'
export const COOEY_MARK_PATH =
  'M12 2.4 L7.41 10.83 L12 12.45 L16.59 10.83 Z ' +
  'M2.2 20.4 L11.35 20.4 L11.06 14.26 L6.79 11.97 Z ' +
  'M21.8 20.4 L17.21 11.97 L12.94 14.26 L12.65 20.4 Z'

export const COOEY: Brand = {
  id: 'cooey',
  name: 'Cooey Club',
  appName: 'Cooey',
  scheme: 'cooey',
  siteUrl: 'https://x.cooey.club',
  defaultService: 'https://bsky.cooey.club',
  apiBase: 'https://chat.cooey.club/api',
  ssoLoginUrl: '/auth/oidc/login?flow=atproto&client=cooey',
  ssoRedeemPath: '/auth/atproto/redeem',
  sso: {label: 'Discord', background: '#5865F2', icon: 'discord'},
  accent: '#C8161D',
  accentBright: '#E03B41',
  markViewBox: COOEY_MARK_VIEWBOX,
  markPath: COOEY_MARK_PATH,
  markElements: null,
  wordmarkWidth: 104,
}

/*
 * Eagle Drive Live: the established chibi bald-eagle tile from the shared
 * Authentik brand (apps/authentik/scripts/sgc/provision-eagledrive-branding.py)
 * -- azure rounded-square chrome, dark swept wings, white head + tail fan,
 * yellow beak, faint coral cheek blush. Palette matches their app's
 * "midnight" theme (bg #0a0b0f, accent #4f8cff, hot #ff6b57).
 *
 * The bird paths use the tile's natural 100x100 coordinates; like the
 * Authentik original, everything scales 1.3x around the canvas center so the
 * bird fills the chrome. Coordinates below are pre-multiplied (x' = 50 +
 * (x-50)*1.3) so we stay plain data with no transform support needed.
 */
const EAGLE_SCALE = 1.3
const es = (v: number) => +(50 + (v - 50) * EAGLE_SCALE).toFixed(2)
const esd = (v: number) => +(v * EAGLE_SCALE).toFixed(2)

export const EAGLEDRIVE: Brand = {
  id: 'eagledrive',
  name: 'Eagle Drive Live',
  appName: 'Eagle Drive',
  scheme: 'eagledrive',
  siteUrl: 'https://x.eagledrive.live',
  // The eagledrive federation PDS (live 2026-07-16); accounts are
  // @<user>.eagledrive.live, provisioned by the eagledrive pds-pro.
  defaultService: 'https://bsky.eagledrive.live',
  // Isolated: eagledrive's login runs entirely on its OWN pds-pro, never cooey.
  apiBase: 'https://admin.eagledrive.live',
  ssoLoginUrl: '/x/login',
  ssoRedeemPath: '/x/redeem',
  // The pill presents the Eagle Drive brand login (auth.eagledrive.live:
  // Facebook/TikTok/password) via delta's client table, and the handoff
  // redeems against the EAGLEDRIVE broker.
  sso: {label: 'Eagle Drive', background: '#4f8cff', icon: 'brand'},
  accent: '#4f8cff',
  accentBright: '#73a4ff',
  markViewBox: '0 0 100 100',
  markPath: null,
  markElements: [
    {
      type: 'rect',
      x: 0,
      y: 0,
      width: 100,
      height: 100,
      rx: 22,
      fill: '#4f8cff',
    },
    // left + right wings
    {
      type: 'path',
      d: `M${es(50)} ${es(52)} Q${es(28)} ${es(38)} ${es(10)} ${es(50)} Q${es(22)} ${es(62)} ${es(50)} ${es(56)} Z`,
      fill: '#0a0b0f',
    },
    {
      type: 'path',
      d: `M${es(50)} ${es(52)} Q${es(72)} ${es(38)} ${es(90)} ${es(50)} Q${es(78)} ${es(62)} ${es(50)} ${es(56)} Z`,
      fill: '#0a0b0f',
    },
    // body
    {
      type: 'ellipse',
      cx: es(50),
      cy: es(62),
      rx: esd(7),
      ry: esd(11),
      fill: '#0a0b0f',
    },
    // white tail fan
    {
      type: 'path',
      d: `M${es(44)} ${es(72)} L${es(50)} ${es(82)} L${es(56)} ${es(72)} Z`,
      fill: '#f5f6f8',
    },
    // white head
    {type: 'circle', cx: es(50), cy: es(40), r: esd(13), fill: '#f5f6f8'},
    // eyes + highlights
    {type: 'circle', cx: es(46), cy: es(39), r: esd(2.2), fill: '#0a0b0f'},
    {type: 'circle', cx: es(54), cy: es(39), r: esd(2.2), fill: '#0a0b0f'},
    {type: 'circle', cx: es(46.7), cy: es(38.3), r: esd(0.8), fill: '#f5f6f8'},
    {type: 'circle', cx: es(54.7), cy: es(38.3), r: esd(0.8), fill: '#f5f6f8'},
    // yellow beak
    {
      type: 'path',
      d: `M${es(47)} ${es(43)} L${es(50)} ${es(49)} L${es(53)} ${es(43)} Z`,
      fill: '#fdc23f',
    },
    // coral cheek blush
    {
      type: 'circle',
      cx: es(43),
      cy: es(44),
      r: esd(1.8),
      fill: '#ff6b57',
      opacity: 0.4,
    },
    {
      type: 'circle',
      cx: es(57),
      cy: es(44),
      r: esd(1.8),
      fill: '#ff6b57',
      opacity: 0.4,
    },
  ],
  wordmarkWidth: 158,
}

/*
 * Yeet FM: a vinyl record -- charcoal disc with fine grooves and a warm amber
 * center label (ties to the vinyl identity of store.yeet.fm / The Collection).
 * Authored 24x24 like the cooey mark so favicon rasterization stays simple.
 */
/**
 * The "Deals" list on yeet's own PDS, owned by @curator.yeet.fm
 * (did:plc:bbtqsidf4neikjqkczsz5zvx), containing @deals.yeet.fm.
 *
 * Why a LIST and not the deals account directly: saved-feed preferences only
 * accept `feed | list | timeline`, so an author feed cannot be pinned as a tab.
 * Wrapping the account in a one-member curate list makes it a first-class,
 * pinnable feed — and lets deal sources be added later with no client rebuild.
 */
export const YEET_DEALS_LIST_URI =
  'at://did:plc:bbtqsidf4neikjqkczsz5zvx/app.bsky.graph.list/3mr72zwpsjc2c'

export const YEET: Brand = {
  id: 'yeet',
  name: 'Yeet FM',
  appName: 'Yeet FM',
  scheme: 'yeetfm',
  siteUrl: 'https://x.yeet.fm',
  // yeet runs its OWN live PDS; password login lands there natively.
  defaultService: 'https://bsky.yeet.fm',
  // sso:null -> these are unused (password-only); placeholder to satisfy the type.
  apiBase: 'https://chat.cooey.club/api',
  ssoLoginUrl: '/auth/oidc/login?flow=atproto&client=yeet',
  ssoRedeemPath: '/auth/atproto/redeem',
  sso: null,
  accent: '#f5a623',
  accentBright: '#ffc14d',
  markViewBox: '0 0 24 24',
  markPath: null,
  markElements: [
    // disc
    {type: 'circle', cx: 12, cy: 12, r: 10.8, fill: '#16181d'},
    // grooves (thin rings)
    {
      type: 'circle',
      cx: 12,
      cy: 12,
      r: 8.7,
      stroke: '#2a2e38',
      strokeWidth: 0.7,
    },
    {
      type: 'circle',
      cx: 12,
      cy: 12,
      r: 6.8,
      stroke: '#2a2e38',
      strokeWidth: 0.7,
    },
    // amber label + spindle hole
    {type: 'circle', cx: 12, cy: 12, r: 4.6, fill: '#f5a623'},
    {type: 'circle', cx: 12, cy: 12, r: 1.1, fill: '#0b0c10'},
  ],
  wordmarkWidth: 84,
  /**
   * Discover is yeet's own deals feed, not Bluesky's global whats-hot.
   * `defaultSavedFeeds` MUST be spelled out: once `feeds` is set, omitting it
   * would fall back to the Bluesky defaults and re-pin whats-hot, contradicting
   * the discover override.
   */
  feeds: {
    defaultSavedFeeds: [
      {type: 'timeline', value: 'following', pinned: true},
      {type: 'list', value: YEET_DEALS_LIST_URI, pinned: true},
    ],
    discoverFeed: {
      kind: 'list',
      uri: YEET_DEALS_LIST_URI,
      label: 'Deals',
    },
  },
}

/*
 * CCISD: the x-style client for Clear Creek ISD (ccisd.me — Houston/Clear
 * Lake, home of the Johnson Space Center). Mark: a clean four-point
 * compass/NASA-vector star, authored 24x24 like the cooey mark. Single path so
 * it tints with the current color (institutional blue #002395 primary, gold
 * #fcc742 highlight via accentBright). Tips sit 11 units from center with the
 * concave waist at the 45-degree diagonals (2.5 units out), so the star is
 * symmetric on both axes and reads balanced at favicon sizes.
 */
export const CCISD_MARK_VIEWBOX = '0 0 24 24'
export const CCISD_MARK_PATH =
  'M12 1 L14.5 9.5 L23 12 L14.5 14.5 L12 23 L9.5 14.5 L1 12 L9.5 9.5 Z'

/**
 * "CCISD Campuses" — the district-curated list of all 45 CCISD campus
 * accounts, owned by @district.ccisd.me
 * (did:plc:krnercwvv3gyul4yvz3yfgxt). This is the district's editorial
 * surface: adding/removing a campus account from the list changes what every
 * user's default feed shows, with no client rebuild.
 */
export const CCISD_CAMPUSES_LIST_URI =
  'at://did:plc:krnercwvv3gyul4yvz3yfgxt/app.bsky.graph.list/3mr6oib3me22k'

export const CCISD: Brand = {
  id: 'ccisd',
  name: 'CCISD',
  appName: 'CCISD',
  scheme: 'ccisd',
  siteUrl: 'https://x.ccisd.me',
  // The district's own PDS; accounts are @<user>.ccisd.me and password
  // login/signup lands there natively (same shape as yeet).
  defaultService: 'https://bsky.ccisd.me',
  // sso:null -> these are unused (password-only). Kept pointed at CCISD's own
  // delta (chat.ccisd.me) so enabling SSO later can never route a district
  // login through cooey's API.
  apiBase: 'https://chat.ccisd.me/api',
  ssoLoginUrl: '/auth/oidc/login?flow=atproto&client=ccisd',
  ssoRedeemPath: '/auth/atproto/redeem',
  sso: null,
  accent: '#002395',
  accentBright: '#fcc742',
  markViewBox: CCISD_MARK_VIEWBOX,
  markPath: CCISD_MARK_PATH,
  markElements: null,
  wordmarkWidth: 64,
  /*
   * District-curated defaults. CCISD's PDS proxies to the real Bluesky
   * AppView, so upstream's default (Bluesky's global "What's Hot" firehose,
   * PINNED) would make live, unmoderated global content the landing view for
   * a K-12 audience of ~39,700 students. Instead: Following first, then the
   * district's own campus list. Bluesky's Discover is neither pinned nor
   * saved, and the Following feed does not spill into it.
   */
  feeds: {
    defaultSavedFeeds: [
      {type: 'timeline', value: 'following', pinned: true},
      {type: 'list', value: CCISD_CAMPUSES_LIST_URI, pinned: true},
    ],
    discoverFeed: {
      kind: 'list',
      uri: CCISD_CAMPUSES_LIST_URI,
      label: 'Campuses',
    },
  },
  /*
   * Runtime override, served by the district's own pds-pro Brand Manager. The
   * values above stay the shipped fallback: they are what a first-ever visit
   * renders, and what the client falls back to whenever admin.ccisd.me is
   * slow, down or answering junk.
   */
  brandConfigUrl: 'https://admin.ccisd.me/api/brand/ccisd.json',
}

export const BRANDS: Record<Brand['id'], Brand> = {
  cooey: COOEY,
  eagledrive: EAGLEDRIVE,
  yeet: YEET,
  ccisd: CCISD,
}
